import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { EmailEvent } from '../../api/models/email-event';
import { EmailMessage } from '../../api/models/email-message';
import { EmailTemplate } from '../../api/models/email-template';
import { InvoiceAttachment } from '../../api/models/invoice-attachment';
import { SendEmailRequest } from '../../api/models/send-email-request';
import { EmailsService } from '../../api/services/emails.service';
import { InvoicesService } from '../../api/services/invoices.service';
import { Invoice } from '../../model/invoice';
import { UsersMap, UsersMapCache } from '../../model/model-map-cache';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { RealtimeNotificationsService } from '../../service/realtime-notifications.service';

@Component({
  selector: 'app-invoice-email-v2-widget',
  templateUrl: './invoice-email-v2-widget.component.html',
  styleUrls: ['./invoice-email-v2-widget.component.scss']
})
export class InvoiceEmailV2WidgetComponent implements OnInit, OnDestroy {

  private invoicePeerService = inject(InvoicePeerService);
  private emailsService = inject(EmailsService);
  private invoicesService = inject(InvoicesService);
  private usersMapCache = inject(UsersMapCache);
  private builder = inject(UntypedFormBuilder);
  private realtime = inject(RealtimeNotificationsService);

  public invoice$: Observable<Invoice>;
  private readonly emailMessagesSubject = new BehaviorSubject<EmailMessage[]>([]);
  public emailMessages$ = this.emailMessagesSubject.asObservable();
  public emailTemplates$: Observable<EmailTemplate[]>;
  private readonly invoiceAttachmentsSubject = new BehaviorSubject<InvoiceAttachment[]>([]);
  public invoiceAttachments$ = this.invoiceAttachmentsSubject.asObservable();
  public usersMap$: Observable<UsersMap>;

  public invoice: Invoice;
  public selectedItem: EmailMessage | null = null;

  public form: UntypedFormGroup;
  public locked = false;
  public added = false;
  public saving = false;

  private readonly ngDestroy$ = new Subject<void>();

  constructor() {
    this.form = this.builder.group({
      emailTemplateId: [null],
      to: [null, Validators.required],
      cc: [null],
      bcc: [null],
      subject: [null, Validators.required],
      message: [null, Validators.required],
      attachmentIds: [[]]
    });
  }

  public ngOnInit(): void {
    this.usersMap$ = this.usersMapCache.map$;
    this.emailTemplates$ = this.getEmailTemplates();

    this.invoice$ = this.invoicePeerService.get().pipe(
      tap(invoice => {
        this.invoice = invoice;
        if (invoice) {
          this.refreshData(invoice.invoiceId);
        } else {
          this.emailMessagesSubject.next([]);
          this.invoiceAttachmentsSubject.next([]);
        }
      })
    );

    this.invoicePeerService.getState().pipe(takeUntil(this.ngDestroy$)).subscribe((state) => {
      this.locked = (state === 'locked');
    });

    // AI: Refresh on EMAIL_CREATED so a freshly persisted email_messages
    // row (e.g. from GenerateOfferWorkflow's CreateOfferEmailMessageActivity)
    // appears in the list without a manual page refresh — the row lands
    // in the DB before SendInvoiceEmailActivity finishes, so waiting for
    // the eventual EMAIL_UPDATED would leave a visible gap.
    this.realtime.events$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(event => {
        if (!this.invoice) {
          return;
        }
        const matchInvoice = (event as { invoice_id?: number }).invoice_id === this.invoice.invoiceId;
        if (!matchInvoice) {
          return;
        }
        if (event.event_type === 'email_created' || event.event_type === 'email_updated') {
          this.refreshEmails(this.invoice.invoiceId);
        } else if (
          event.event_type === 'invoice_attachment_created' ||
          event.event_type === 'invoice_attachment_updated'
        ) {
          this.refreshAttachments(this.invoice.invoiceId);
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  public add(): void {
    this.invoicePeerService.lock();
    this.added = true;
    this.form.reset({
      attachmentIds: []
    });
  }

  public revert(): void {
    this.invoicePeerService.unlock();
    this.added = false;
    this.selectedItem = null;
  }

  public save(): void {
    if (this.form.invalid || this.saving) {
      return;
    }

    this.saving = true;

    const to = this.parseRecipients(this.form.value.to);
    const cc = this.parseRecipients(this.form.value.cc);
    const bcc = this.parseRecipients(this.form.value.bcc);

    const request: SendEmailRequest = {
      to,
      subject: this.form.value.subject,
      content: this.form.value.message,
      attachment_ids: this.form.value.attachmentIds,
      template_id: this.form.value.emailTemplateId?.id,
      ...(cc.length > 0 && { cc }),
      ...(bcc.length > 0 && { bcc })
    };

    this.emailsService.sendEmail({ id: this.invoice.invoiceId, body: request }).subscribe({
      next: () => {
        this.invoicePeerService.unlock();
        this.saving = false;
        this.added = false;
        this.selectedItem = null;
        this.refreshEmails(this.invoice.invoiceId);
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  public itemClicked(item: EmailMessage): void {
    this.selectedItem = (item !== this.selectedItem ? item : null);
  }

  public templateChange(event: MatSelectChange): void {
    const template: EmailTemplate = event.value;

    if (!template) {
      this.form.patchValue({
        subject: null,
        message: null
      });
      return;
    }

    this.emailsService.renderEmailTemplate({
      body: {
        invoice_id: this.invoice.invoiceId,
        template_id: template.id
      }
    }).subscribe(rendered => {
      this.form.patchValue({
        subject: rendered.subject,
        message: rendered.content
      });
    });
  }

  public getStatusClass(status: string): string {
    switch (status) {
      case 'delivered':
        return 'status-success';
      case 'failed':
      case 'bounced':
        return 'status-failure';
      default:
        return '';
    }
  }

  public getSortedEvents(events: EmailEvent[]): EmailEvent[] {
    if (!events) {
      return [];
    }
    return [...events].sort((a, b) => new Date(a.event_at || a.created_at).getTime() - new Date(b.event_at || b.created_at).getTime());
  }

  private parseRecipients(value: string | null): string[] {
    if (!value) {
      return [];
    }
    return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }

  private refreshData(invoiceId: number): void {
    this.refreshEmails(invoiceId);
    this.refreshAttachments(invoiceId);
  }

  private refreshEmails(invoiceId: number): void {
    this.emailsService.listEmails({ id: invoiceId }).subscribe(emails => {
      this.emailMessagesSubject.next(emails);
    });
  }

  private refreshAttachments(invoiceId: number): void {
    this.invoicesService.listInvoiceAttachments({ invoiceID: invoiceId }).subscribe(attachments => {
      // AI: Sort by created_at desc and filter out hidden attachments.
      const sorted = attachments
        .filter(a => !a.is_hidden)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      this.invoiceAttachmentsSubject.next(sorted);
    });
  }

  private getEmailTemplates(): Observable<EmailTemplate[]> {
    return this.emailsService.listEmailTemplates().pipe(
      // AI: Sort templates by name client-side.
      map(templates => templates.sort((a, b) => a.name.localeCompare(b.name)))
    );
  }
}
